import { resolveTeacherPlan } from '../config/teacherPlans';
import { initializeApp, getApp, FirebaseApp } from 'firebase/app';
import { Auth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithRedirect, setPersistence, signInWithPopup } from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  orderBy,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  collectionGroup,
  deleteDoc,
  Timestamp,
  serverTimestamp,
  increment,
  addDoc,
  DocumentData,
  QueryDocumentSnapshot,
  Firestore
} from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { browserLocalPersistence } from 'firebase/auth';
import type { CharacterStats } from '../types/character';

// Firebase 설정
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Firebase 초기화
let app: FirebaseApp;
try {
  app = getApp();
} catch {
  app = initializeApp(firebaseConfig);
}

// Firebase 서비스 초기화
export { app };
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);

/** 새로고침 직후 Firestore 요청에 인증 토큰이 붙도록 보장 */
export async function waitForAuthReady(): Promise<void> {
  await auth.authStateReady();
}
export const storage: FirebaseStorage = getStorage(app);

// Firebase 인증 설정
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Auth persistence 설정 중 오류:', error);
});

// 소셜 로그인 프로바이더 설정
export const googleProvider = new GoogleAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');

// 타입 정의
export interface BaseUser {
  id: string;
  email: string;
  name: string;
  role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
  schoolId: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  studentId?: string;
}

export interface Stamp {
  subject: string;
  count: number;
  updatedAt: Date;
}

export interface ClassSetting {
  grade: number;
  class: number;
  isActive: boolean;
}

export interface ClassRole {
  id: string;
  schoolId: string;
  classId: string;
  name: string;
  description: string;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  isActive: boolean;
  isPointManager?: boolean;
  pointAutoPay: boolean;
  maxCount: number;
  requireBothApprovals: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface User {
  uid: string;  // 소셜 로그인 사용자의 경우에만 존재
  email: string;
  name: string;
  role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
  schoolId: string;
  teacherId?: string;
  loginType: 'local' | 'social';
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  roles?: string[];
  items?: any[];
  classId?: string;
  photoURL?: string;
  /** 교사 요금제. 학생은 담당 교사 요금제를 따른다. 없으면 기존 계정은 학교 요금제로 본다. */
  plan?: 'free' | 'class' | 'school';
  subjectId?: string;  // 교과 ID 추가
  lastLoginAt?: Date;
  fcmTokens?: string[];
}

export interface Teacher extends User {
  role: 'teacher' | 'schoolAdmin' | 'superAdmin';
  subject?: string;
  subjectId?: string;  // null을 제거하고 undefined만 허용
  homeroomClass?: {
    grade: number;
    class: number;
  };
  stampClasses: Array<{
    grade: number;
    class: number;
    isActive: boolean;
  }>;
  classRoles: string[];
  isAdmin?: boolean;
  stampImageUrl?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  // 프로필 커스터마이징 필드들
  bio?: string;
  contactEmail?: string;
  phone?: string;
  website?: string;
  location?: string;
  
  // 학력 정보
  education?: Array<{
    id: string;
    degree: string; // 학위 (학사, 석사, 박사 등)
    major: string; // 전공
    school: string; // 학교명
    graduationYear: number; // 졸업년도
    isGraduated: boolean; // 졸업 여부
  }>;
  
  // 강의 이력
  teachingHistory?: Array<{
    id: string;
    subject: string; // 과목명
    grade: number; // 학년
    class: number; // 반
    school: string; // 학교명
    startDate: string; // 시작일
    endDate?: string; // 종료일 (현재 강의 중이면 없음)
    isCurrent: boolean; // 현재 강의 중인지
    description?: string; // 설명
  }>;
}

export interface Student extends User {
  name: string;
  username: string;
  /** 저장하지 않습니다. 가입 화면의 입력값으로만 쓰고, 문서에는 넣지 않습니다. */
  password?: string;
  email: string;
  grade: number;
  class: number;
  number: number;
  points?: number;
  stamps?: {
    subjectId: string;
    stampCount: number;
  }[];
  isPointManager?: boolean;
  assignedRoles?: {
    roleId: string;
    assignedAt: Date;
    isActive: boolean;
  }[];
  createdAt: Date;
  updatedAt?: Date;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  /** 캐릭터 능력치 (학급 학생 문서, 1~10, 필드: atk/def/hp/mp) */
  characterStats?: CharacterStats;
  /** 캐릭터 레벨 (1~99) */
  characterLevel?: number;
}

export interface SchoolAdmin extends User {
  role: 'schoolAdmin';
}

export interface SuperAdmin extends User {
  role: 'superAdmin';
  /** 담임을 맡을 경우 학년/반 정보 */
  homeroomClass?: {
    grade: number;
    class: number;
  };
}

export interface Class {
  id: string;
  name: string;
  grade: number;
  classNumber: number;
  teacherId: string;
  teacherName: string;
  schoolId: string;
  studentIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  classId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Point {
  id: string;
  studentId: string;
  type: 'earn' | 'use';
  amount: number;
  description: string;
  date: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Subject {
  id: string;
  name: string;
  description: string;
  classId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface School {
  id: string;
  name: string;
  address: string;
  phone: string;
  code: string;
  type: 'elementary' | 'middle' | 'high';
  adminId?: string;
  maxClassCount: number;
  gradeClasses: {
    [grade: number]: number; // 학년: 학급 수
  };
  logo?: string; // 학교 로고 이미지 URL
  isActive: boolean;
  logo?: string; // 학교 로고 이미지 URL
  /** 나이스 연동용 시도교육청코드 (ATPT_OFCDC_SC_CODE) */
  neisOfficeCode?: string;
  /** 나이스 연동용 표준학교코드 (SD_SCHUL_CODE) */
  neisSchoolCode?: string;
  /** 컴시간알리미 학교 코드 */
  comciganSchoolCode?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface PointTemplate {
  id: string;
  name: string;
  amount: number;
  reason: string;
  teacherId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SpecialPointTemplate {
  id: string;
  title: string;
  description: string;
  pointAmount: number;
  schoolId: string;
  classId: string;
  createdBy: string; // teacher uid
  createdAt: Date;
  updatedAt: Date;
  studentIds?: string[]; // 선택적으로 저장할 수 있는 학생 목록
}

export interface PointHistory {
  id: string;
  studentId: string;
  amount: number;
  reason: string;
  date: Date;
  type: 'oneTime' | 'daily' | 'monthly';
  teacherId: string;
  schoolId: string;
  classId: string;
  createdAt: Date;
  /** 급여 지급 시 총액(세전). 없으면 amount만 사용(레거시) */
  grossAmount?: number;
  /** 항목별 공제 세액 */
  taxLines?: { label: string; amount: number }[];
  totalTax?: number;
  kind?: 'salary';
}

export const COLLECTIONS = {
  STUDENTS: 'schools/{schoolId}/classes/{classId}/students',
  TEACHERS: 'schools/{schoolId}/teachers',
  SCHOOLS: 'schools',
  CLASSES: 'schools/{schoolId}/classes',
  POINT_APPROVALS: 'schools/{schoolId}/classes/{classId}/pointApprovals',
  POINT_HISTORIES: 'schools/{schoolId}/classes/{classId}/pointHistories',
  CLASS_POINT_HISTORIES: 'schools/{schoolId}/classes/{classId}/classPointHistories',
  // 학급 코드는 학생들이 로그인 할 때 바로 접근해야해서 이게 맞다!!
  CLASS_CODES: 'classCodes', 
  POINT_TEMPLATES: 'schools/{schoolId}/classes/{classId}/pointTemplates',
  SPECIAL_POINT_TEMPLATES: 'schools/{schoolId}/classes/{classId}/specialPointTemplates',
  STUDENT_REQUESTS: 'student_requests',
  TEACHER_REQUESTS: 'teacher_requests',
  SUBJECT_CODES: 'subjectCodes',
  CLASS_ROLES: 'schools/{schoolId}/classes/{classId}/classRoles',
  PURCHASES: 'schools/{schoolId}/classes/{classId}/purchases',
  STORECLASSITEMS: 'schools/{schoolId}/classes/{classId}/storeclassItems',
  STORESUBJECTITEMS: 'schools/{schoolId}/subjects/{subjectId}/storesubjectItems',
  STAMP_TEMPLATES: 'schools/{schoolId}/subjects/{subjectId}/stampTemplates',
  STAMP_HISTORIES: 'schools/{schoolId}/subjects/{subjectId}/stampHistories',
  ITEM_USAGES: 'schools/{schoolId}/classes/{classId}/itemUsages',
  ITEM_HISTORIES: 'schools/{schoolId}/classes/{classId}/itemHistories',
  NOTES: 'notes',
  /** 학급 마니또: 문서 ID = 챙겨주는 사람(학생 uid), 내용 = 대상 학생 정보 */
  MANITO_ASSIGNMENTS: 'schools/{schoolId}/classes/{classId}/manitoAssignments',
  /** 학급 급여 세금(공제) 설정: 문서 id 예: default */
  SALARY_TAX_SETTINGS: 'schools/{schoolId}/classes/{classId}/salaryTaxSettings',
  /** 학급 캐릭터 베이스/추가 파츠 카탈로그(선택, 상점 코스메틱과 병행 가능) */
  CHARACTER_ITEMS: 'schools/{schoolId}/classes/{classId}/characterItems',
  /** MakeCode Arcade 에셋 팩 공유(arcade.makecode.com 프로젝트 URL) */
  ARCADE_ASSET_PACKS: 'arcadeAssetPacks',
  /** 생일 롤링페이퍼(문서 ID = 비밀 토큰, QR·공개 링크용) */
  BIRTHDAY_ROLLING_PAPERS: 'birthdayRollingPapers',
  /** 교사 연수 공개 허브 프로필 (문서 ID = ownerUid) */
  TRAINING_HOSTS: 'trainingHosts',
  /** 공개 slug 예약 (문서 ID = slug, ownerUid 필드) */
  TRAINING_HOST_SLUGS: 'trainingHostSlugs',
  /** 교사별 연수 프로그램 목록 */
  TRAINING_PROGRAMS: 'trainingPrograms',
} as const;

// Firestore 문서 ID 생성 함수
export const generateId = (prefix: string): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 학생 문서 필드·경로에서 학급 문서 ID(`schoolId_grade_class` 등)를 일관되게 복원합니다.
 * classId 누락 시 `grade_class`만 쓰면 실제 `schools/.../classes/{schoolId}_g_c` 경로와 어긋납니다.
 */
export function resolveStudentClassId(
  studentDoc: { ref: { path: string } },
  studentData: Record<string, any>
): string {
  // 실제 학생 문서가 위치한 학급(schools/.../classes/{classId}/students/...)이 저장된 classId 필드보다 신뢰됨
  const parts = studentDoc.ref.path.split('/');
  if (parts.length >= 6 && parts[0] === 'schools' && parts[2] === 'classes' && parts[4] === 'students') {
    return parts[3];
  }

  const fromField = studentData.classId as string | undefined;
  if (fromField) return fromField;

  const schoolId = studentData.schoolId as string | undefined;
  const grade = studentData.grade;
  const classNum = studentData.class;
  if (schoolId != null && grade != null && classNum != null) {
    return `${schoolId}_${Number(grade)}_${Number(classNum)}`;
  }
  if (grade != null && classNum != null) {
    return `${Number(grade)}_${Number(classNum)}`;
  }
  return '';
}

/** 담임 교사: classId 필드 누락 시 homeroomClass + schoolId로 학급 문서 ID 복원 */
export function resolveTeacherClassId(teacher: {
  schoolId?: string;
  classId?: string;
  homeroomClass?: { grade: number; class: number };
}): string | undefined {
  const cid = teacher.classId?.trim();
  if (cid) return cid;
  if (
    teacher.schoolId &&
    teacher.homeroomClass != null &&
    teacher.homeroomClass.grade != null &&
    teacher.homeroomClass.class != null
  ) {
    return `${teacher.schoolId}_${teacher.homeroomClass.grade}_${teacher.homeroomClass.class}`;
  }
  return undefined;
}

/** `users/{uid}`에 저장되는 역할 (Auth·가입·승인 플로우와 동일) */
export type UsersRootRole = 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';

/**
 * 루트 `users/{uid}` 프로필을 merge로 갱신합니다. 신규면 createdAt·fcmTokens·lastLoginAt을 둡니다.
 * 가입/승인 시 getUser이 users를 우선 읽도록 하기 위해 호출합니다.
 */
export async function upsertUsersRootProfile(params: {
  uid: string;
  email: string;
  name: string;
  role: UsersRootRole;
  schoolId: string;
  loginType?: string;
  isActive?: boolean;
  photoURL?: string | null;
}): Promise<void> {
  const {
    uid,
    email,
    name,
    role,
    schoolId,
    loginType = 'local',
    isActive = true,
    photoURL = null,
  } = params;
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  const payload: Record<string, unknown> = {
    uid,
    email,
    name,
    role,
    schoolId,
    loginType,
    isActive,
    updatedAt: serverTimestamp(),
    photoURL,
  };
  if (!snap.exists()) {
    payload.createdAt = serverTimestamp();
    payload.lastLoginAt = null;
    payload.fcmTokens = [];
  }
  await setDoc(userRef, payload, { merge: true });
}

// User 관련 함수
export const getUser = async (userId: string): Promise<User | null> => {
  try {
    // 1. 먼저 users 컬렉션에서 확인 (마이그레이션 후)
    const userRef = doc(db, `users/${userId}`);
    const userDoc = await getDoc(userRef);

    // role이 없는 users 문서(예: 함수가 plan만 써서 생긴 문서)는 없는 것으로 보고 레거시 경로로 찾는다.
    if (userDoc.exists() && userDoc.data().role) {
      const userData = userDoc.data();
      console.log('users 컬렉션에서 사용자 데이터 발견:', userData);
      
      // 기본 사용자 정보 반환
      const baseUser: User = {
        uid: userId,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        schoolId: userData.schoolId,
        loginType: userData.loginType || 'local',
        isActive: userData.isActive ?? true,
        createdAt: userData.createdAt?.toDate() || new Date(),
        updatedAt: userData.updatedAt?.toDate() || new Date(),
        photoURL: userData.photoURL,
        lastLoginAt: userData.lastLoginAt?.toDate(),
        fcmTokens: userData.fcmTokens || []
      };
      
      // 교사인 경우 추가 정보 조회
      if (userData.role === 'teacher' || userData.role === 'schoolAdmin' || userData.role === 'superAdmin') {
        const teacherQuery = query(
          collectionGroup(db, 'teachers'),
          where('uid', '==', userId)
        );
        const teacherSnapshot = await getDocs(teacherQuery);
        
        if (!teacherSnapshot.empty) {
          const teacherData = teacherSnapshot.docs[0].data();
          const mergedSchoolId =
            (teacherData.schoolId as string | undefined) || baseUser.schoolId;
          const classId =
            teacherData.homeroomClass && mergedSchoolId
              ? `${mergedSchoolId}_${teacherData.homeroomClass.grade}_${teacherData.homeroomClass.class}`
              : undefined;
          return {
            ...baseUser,
            schoolId: mergedSchoolId,
            subject: teacherData.subject,
            subjectId: teacherData.subjectId || teacherData.subject || '',
            homeroomClass: teacherData.homeroomClass,
            classId,
            stampClasses: teacherData.stampClasses || [],
            classRoles: teacherData.classRoles || [],
            isAdmin: teacherData.isAdmin,
            stampImageUrl: teacherData.stampImageUrl,
            bio: teacherData.bio,
            contactEmail: teacherData.contactEmail,
            phone: teacherData.phone,
            website: teacherData.website,
            location: teacherData.location,
            education: teacherData.education,
            teachingHistory: teacherData.teachingHistory,
            plan: resolveTeacherPlan(teacherData.plan, userData.role)
          } as Teacher;
        }
      }
      
      // 학생인 경우 추가 정보 조회
      if (userData.role === 'student') {
        const studentQuery = query(
          collectionGroup(db, 'students'),
          where('uid', '==', userId)
        );
        const studentSnapshot = await getDocs(studentQuery);
        
        if (!studentSnapshot.empty) {
          const studentDocSnap = studentSnapshot.docs[0];
          const studentData = studentDocSnap.data();
          const assignedRoles = studentData.assignedRoles ? studentData.assignedRoles.map((role: any) => ({
            roleId: role.roleId,
            assignedAt: role.assignedAt?.toDate() || new Date(),
            isActive: role.isActive
          })) : [];
          
          let plan: 'free' | 'class' | 'school' = 'school';
          const teacherId = studentData.teacherId as string | undefined;
          const studentSchoolId = (studentData.schoolId as string | undefined) || baseUser.schoolId;
          if (teacherId && studentSchoolId) {
            // 교사 문서를 못 읽어도 로그인은 이어간다. 요금제 제한은 서버 함수가 다시 확인한다.
            try {
              const teacherSnap = await getDoc(doc(db, `schools/${studentSchoolId}/teachers/${teacherId}`));
              plan = resolveTeacherPlan(teacherSnap.data()?.plan, teacherSnap.data()?.role);
            } catch (planError) {
              console.warn('담당 교사 요금제 조회 실패:', planError);
            }
          }

          return {
            ...baseUser,
            plan,
            teacherId: studentData.teacherId,
            grade: studentData.grade,
            class: studentData.class,
            number: studentData.number,
            stamps: studentData.stamps || [],
            username: studentData.username,
            points: studentData.points || 0,
            accountLinked: studentData.accountLinked || false,
            isPointManager: studentData.isPointManager || false,
            roles: studentData.roles || [],
            assignedRoles: assignedRoles,
            classId: resolveStudentClassId(studentDocSnap, studentData),
            approvalStatus: studentData.approvalStatus || 'pending'
          } as Student;
        }
      }
      
      return baseUser;
    }
    
    // 2. users 컬렉션에 없으면 기존 방식으로 조회 (마이그레이션 전 호환성)
    if (import.meta.env.DEV) {
      console.debug('[getUser] users/{uid} 문서 없음 → teachers/students 레거시 경로로 조회');
    }
    
    // 먼저 teacher 컬렉션에서 확인
    const teacherQuery = query(
      collectionGroup(db, 'teachers'),
      where('uid', '==', userId)
    );
    const teacherSnapshot = await getDocs(teacherQuery);
    
    if (!teacherSnapshot.empty) {
      const teacherDoc = teacherSnapshot.docs[0];
      const userData = teacherDoc.data();
      return {
        uid: userId,
        email: userData.email,
        name: userData.name,
        role: userData.role || 'teacher',
        schoolId: userData.schoolId,
        subject: userData.subject,
        subjectId: userData.subjectId || userData.subject || '',
        homeroomClass: userData.homeroomClass,
        stampClasses: userData.stampClasses || [],
        stampImageUrl: userData.stampImageUrl,
        isActive: userData.isActive ?? true,
        createdAt: userData.createdAt?.toDate() || new Date(),
        updatedAt: userData.updatedAt?.toDate() || new Date(),
        classId: userData.classId || '',
        plan: resolveTeacherPlan(userData.plan, userData.role)
      } as Teacher;
    }

    // student 컬렉션에서 확인
    const studentQuery = query(
      collectionGroup(db, 'students'),
      where('uid', '==', userId)
    );
    const studentSnapshot = await getDocs(studentQuery);
    
    if (!studentSnapshot.empty) {
      const studentDoc = studentSnapshot.docs[0];
      const userData = studentDoc.data();
      
      const assignedRoles = userData.assignedRoles ? userData.assignedRoles.map((role: any) => ({
        roleId: role.roleId,
        assignedAt: role.assignedAt?.toDate() || new Date(),
        isActive: role.isActive
      })) : [];
      
      return {
        uid: userId,
        email: userData.email,
        name: userData.name,
        role: 'student',
        schoolId: userData.schoolId,
        teacherId: userData.teacherId,
        grade: userData.grade,
        class: userData.class,
        number: userData.number,
        stamps: userData.stamps || [],
        loginType: userData.loginType || 'local',
        username: userData.username,
        points: userData.points || 0,
        accountLinked: userData.accountLinked || false,
        isPointManager: userData.isPointManager || false,
        roles: userData.roles || [],
        assignedRoles: assignedRoles,
        isActive: userData.isActive ?? true,
        createdAt: userData.createdAt?.toDate() || new Date(),
        updatedAt: userData.updatedAt?.toDate() || new Date(),
        classId: resolveStudentClassId(studentDoc, userData),
        approvalStatus: userData.approvalStatus || 'pending'
      } as Student;
    }
    
    return null;
  } catch (error) {
    console.error('getUser 함수에서 오류 발생:', error);
    return null;
  }
};

export const getUsersByClass = async (schoolId: string, grade: string, class_: string): Promise<Student[]> => {
  const q = query(
    collection(db, `schools/${schoolId}/classes/${grade}_${class_}/students`),
    orderBy('number', 'asc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const userData = doc.data();
    return {
      uid: doc.id,
      localId: doc.id,
      email: userData.email,
      name: userData.name,
      role: 'student',
      schoolId: userData.schoolId,
      teacherId: userData.teacherId,
      grade: userData.grade,
      class: userData.class,
      number: userData.number,
      stamps: userData.stamps || [],
      loginType: userData.loginType || 'local',
      username: userData.username,
      points: userData.points || 0,
      accountLinked: userData.accountLinked || false,
      isPointManager: userData.isPointManager || false,
      roles: userData.roles || [],
      assignedRoles: userData.assignedRoles || [],
      isActive: userData.isActive ?? true,
      createdAt: userData.createdAt?.toDate() || new Date(),
      updatedAt: userData.updatedAt?.toDate() || new Date(),
      classId: userData.classId || '',
      approvalStatus: userData.approvalStatus || 'pending'
    } as Student;
  });
};

// Stamp 관련 함수
export const getStampsByStudent = async (studentId: string, subject: string): Promise<Stamp[]> => {
  const q = query(
    collection(db, COLLECTIONS.STUDENTS, studentId, 'stamps'),
    where('studentId', '==', studentId),
    where('subject', '==', subject),
    orderBy('date', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      subject: data.subject,
      count: data.count,
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Stamp;
  });
};

// Point 관련 함수
export const getPointsByStudent = async (studentId: string): Promise<Point[]> => {
  const q = query(
    collection(db, COLLECTIONS.STUDENTS, studentId, 'points'),
    where('studentId', '==', studentId),
    orderBy('date', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      studentId: data.studentId,
      type: data.type,
      amount: data.amount,
      description: data.description,
      date: data.date?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Point;
  });
};

export const getStudentTotalPoints = async (studentId: string): Promise<number> => {
  const points = await getPointsByStudent(studentId);
  return points.reduce((total, point) => {
    return total + (point.type === 'earn' ? point.amount : -point.amount);
  }, 0);
};

/** 학급 포인트 등수 기준: balance = 보유 포인트, totalEarned = 총 벌어든 포인트만 */
export const getClassPointRankingMode = async (
  schoolId: string,
  classId: string
): Promise<'balance' | 'totalEarned'> => {
  const classRef = doc(db, 'schools', schoolId, 'classes', classId);
  const classDoc = await getDoc(classRef);
  const mode = classDoc.data()?.pointRankingMode;
  return mode === 'totalEarned' ? 'totalEarned' : 'balance';
};

/** 학급 포인트 이력에서 학생별 총 벌어든 포인트(양수 amount 합계) 반환 */
export const getTotalEarnedPointsByStudent = async (
  schoolId: string,
  classId: string
): Promise<Record<string, number>> => {
  const historyRef = collection(db, 'schools', schoolId, 'classes', classId, 'pointHistories');
  const snapshot = await getDocs(historyRef);
  const byStudent: Record<string, number> = {};
  snapshot.docs.forEach((d) => {
    const data = d.data();
    const studentId = data.studentId || data.uid;
    const amount = typeof data.amount === 'number' ? data.amount : 0;
    if (!studentId) return;
    if (amount > 0) {
      byStudent[studentId] = (byStudent[studentId] || 0) + amount;
    }
  });
  return byStudent;
};

/** 학급 포인트 조회 (선생님이 학급에 준 별도 포인트 풀. 개인 포인트 합과 무관) */
export const getClassPoints = async (
  schoolId: string,
  classId: string
): Promise<number> => {
  const classRef = doc(db, 'schools', schoolId, 'classes', classId);
  const classDoc = await getDoc(classRef);
  const data = classDoc.data();
  const value = data?.classPoints;
  return typeof value === 'number' ? value : 0;
};

/** 학급 포인트 지급/차감 (amount: 양수=지급, 음수=차감) 및 이력 추가 */
export const addClassPoints = async (
  schoolId: string,
  classId: string,
  amount: number,
  options: { reason: string; teacherId: string; teacherName: string }
): Promise<void> => {
  const classRef = doc(db, 'schools', schoolId, 'classes', classId);
  const historyRef = collection(db, 'schools', schoolId, 'classes', classId, 'classPointHistories');
  await updateDoc(classRef, { classPoints: increment(amount) });
  await addDoc(historyRef, {
    amount,
    reason: options.reason,
    teacherId: options.teacherId,
    teacherName: options.teacherName,
    createdAt: serverTimestamp(),
  });
};

export interface ClassPointHistoryItem {
  id: string;
  amount: number;
  reason: string;
  teacherId: string;
  teacherName: string;
  createdAt: Date;
}

/** 학급 포인트 이력 목록 조회 (최신순) */
export const getClassPointHistories = async (
  schoolId: string,
  classId: string,
  limitCount: number = 50
): Promise<ClassPointHistoryItem[]> => {
  const historyRef = collection(db, 'schools', schoolId, 'classes', classId, 'classPointHistories');
  const q = query(historyRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.slice(0, limitCount).map((d) => {
    const data = d.data();
    return {
      id: d.id,
      amount: typeof data.amount === 'number' ? data.amount : 0,
      reason: data.reason || '',
      teacherId: data.teacherId || '',
      teacherName: data.teacherName || '',
      createdAt: data.createdAt?.toDate?.() || new Date(),
    };
  });
};

// Subject 관련 함수
export const getSubjectsByClass = async (classId: string): Promise<Subject[]> => {
  const q = query(collection(db, COLLECTIONS.SUBJECT_CODES), where('classId', '==', classId));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      description: data.description,
      classId: data.classId,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as Subject;
  });
};

// Firestore 타입 가드 함수들
export const isTeacher = (user: User | null | undefined): user is Teacher => {
  return !!user && (user.role === 'teacher' || user.role === 'superAdmin' || user.role === 'schoolAdmin');
};

export const isStudent = (user: User | null | undefined): user is Student => {
  return !!user && user.role === 'student';
};

export const isSchoolAdmin = (user: User | null | undefined): user is SchoolAdmin => {
  return !!user && (user.role === 'schoolAdmin' || user.role === 'superAdmin');
};

export const isSuperAdmin = (user: User | null | undefined): user is SuperAdmin => {
  return !!user && user.role === 'superAdmin';
};

// Firestore 데이터 변환 함수들
export const convertToTeacher = (data: any): Teacher => ({
  uid: data.id,  // Firebase Auth의 uid
  email: data.email || '',
  name: data.name,
  role: data.role || 'teacher',  // role이 없으면 기본값 'teacher'
  schoolId: data.schoolId,
  subject: data.subject,
  subjectId: data.subjectId || data.subject || '',  // subjectId가 없으면 subject를 사용
  homeroomClass: data.homeroomClass ? {
    grade: Number(data.homeroomClass.grade),
    class: Number(data.homeroomClass.class)
  } : undefined,
  stampClasses: data.stampClasses || [],
  stampImageUrl: data.stampImageUrl,
  classRoles: data.classRoles || [],
  isActive: data.isActive ?? true,
  createdAt: data.createdAt?.toDate() || new Date(),
  updatedAt: data.updatedAt?.toDate() || new Date(),
  classId: data.classId || '',
  loginType: data.loginType || 'local',
  plan: resolveTeacherPlan(data.plan, data.role)
});

export const convertToStudent = (data: any): Student => ({
  uid: data.loginType === 'social' ? data.id : undefined,
  email: data.email || '',
  name: data.name,
  role: 'student',
  schoolId: data.schoolId,
  teacherId: data.teacherId,
  grade: Number(data.grade),
  class: Number(data.class),
  number: Number(data.number),
  stamps: data.stamps || [],
  loginType: data.loginType || 'local',
  username: data.username,
  points: data.points || 0,
  isPointManager: data.isPointManager || false,
  roles: data.roles || [],
  assignedRoles: data.assignedRoles || [],
  isActive: data.isActive ?? true,
  createdAt: data.createdAt?.toDate() || new Date(),
  updatedAt: data.updatedAt?.toDate() || new Date(),
  classId: data.classId || '',
  approvalStatus: data.approvalStatus || 'pending'
});

// School 관련 함수
export const getSchool = async (schoolId: string): Promise<School | null> => {
  const docRef = doc(db, COLLECTIONS.SCHOOLS, schoolId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    address: data.address,
    phone: data.phone,
    code: data.code,
    type: data.type,
    maxClassCount: data.maxClassCount,
    gradeClasses: data.gradeClasses || {},
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
};

export const getSchools = async (): Promise<School[]> => {
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.SCHOOLS));
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      address: data.address,
      phone: data.phone,
      code: data.code,
      type: data.type,
      maxClassCount: data.maxClassCount,
      gradeClasses: data.gradeClasses || {},
      isActive: data.isActive ?? true,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  });
};

export const getSchoolByCode = async (code: string): Promise<School | null> => {
  const schoolsRef = collection(db, COLLECTIONS.SCHOOLS);
  const q = query(schoolsRef, where('code', '==', code));
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return null;
  }

  const doc = querySnapshot.docs[0];
  const data = doc.data();
  console.log('학교 데이터:', data);
  
  return {
    id: doc.id,
    name: data.name,
    address: data.address,
    phone: data.phone,
    code: data.code,
    type: data.type,
    maxClassCount: data.maxClassCount,
    gradeClasses: data.gradeClasses || {},
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
};

export const createPointHistory = async (data: Omit<PointHistory, 'id' | 'createdAt'>): Promise<PointHistory> => {
  const pointHistoryRef = doc(collection(db, `schools/${data.schoolId}/classes/${data.classId}/pointHistories`));
  const pointHistoryData = {
    ...data,
    createdAt: new Date(),
  };

  await setDoc(pointHistoryRef, pointHistoryData);
  return {
    id: pointHistoryRef.id,
    ...pointHistoryData,
  };
};

export const getPointHistories = async (schoolId: string, studentId?: string): Promise<PointHistory[]> => {
  let q = query(collection(db, `schools/${schoolId}/pointHistories`));
  
  if (studentId) {
    q = query(q, where('studentId', '==', studentId));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    date: doc.data().date.toDate(),
    createdAt: doc.data().createdAt.toDate(),
  })) as PointHistory[];
};

export const getTeachers = async (): Promise<Teacher[]> => {
  try {
    const teachersRef = collectionGroup(db, 'teachers');
    const snapshot = await getDocs(teachersRef);
    return snapshot.docs.map(doc => ({
      uid: doc.id,
      email: doc.data().email || '',
      name: doc.data().name,
      role: doc.data().role || 'teacher',
      schoolId: doc.data().schoolId,
      subject: doc.data().subject || null,
      subjectId: doc.data().subjectId || null,
      homeroomClass: doc.data().homeroomClass,
      stampClasses: doc.data().stampClasses || [],
      classRoles: doc.data().classRoles || [],
      isActive: doc.data().isActive ?? true,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      loginType: doc.data().loginType || 'local'
    }));
  } catch (error) {
    console.error('Error getting teachers:', error);
    return [];
  }
};

// 토큰 디코딩 함수
export const decodeToken = async (token: string) => {
  try {
    const decodedToken = await auth.currentUser?.getIdTokenResult();
    return decodedToken;
  } catch (error) {
    console.error('Error decoding token:', error);
    throw error;
  }
}; 